/* eslint-disable max-len */
/**
 * Awvent - Users API
 *
 * Hand-authored client for User.Api/GroupController (GAP-BACKEND-ADMIN-005).
 * GAP-ADMIN-PTG-002 — the audience picker lists Groups via groupGet; full CRUD
 * lands with the Teams & Groups page (GAP-ADMIN-PTG-004).
 *
 * Note: The Group wire shapes intentionally differ from Team. POST nests its DTO
 * under `details` (GroupCreateCommand → GroupCreateDto); PUT is FLAT (GroupUpdateCommand
 * exposes Id/Name/Description/MemberIds at the top level). Backend reference:
 * User.Contracts/Commands/GroupCommands.cs.
 */
import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

import { GroupDtoListWebApiResponse } from '../model/groupDtoListWebApiResponse';
import { GroupDto } from '../model/groupDto';

import { Configuration } from '../../../../configuration';
import { BASE_PATH } from '../../../../variables';

export interface GroupCreateCommand {
    details: {
        name: string;
        description?: string;
        memberIds: string[];
    };
}

export interface GroupUpdateCommand {
    id: string;
    name: string;
    description?: string;
    memberIds: string[];
}

export interface GroupDtoWebApiResponse {
    isSuccess?: boolean;
    message?: string;
    result?: GroupDto;
    erros?: Array<{ key?: string; description?: string }>;
}

export interface BooleanWebApiResponse {
    isSuccess?: boolean;
    message?: string;
    result?: boolean;
    erros?: Array<{ key?: string; description?: string }>;
}

@Injectable()
export class GroupService {

    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    protected basePath = environment.api.users;

    constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    private buildHeaders(xTenantId?: string, withContentType = false): HttpHeaders {
        let headers = this.defaultHeaders;
        if (xTenantId) {
            headers = headers.set('x-tenant-id', String(xTenantId));
        }
        if (this.configuration.accessToken) {
            const accessToken = typeof this.configuration.accessToken === 'function'
                ? this.configuration.accessToken()
                : this.configuration.accessToken;
            headers = headers.set('Authorization', 'Bearer ' + accessToken);
        }
        const accepts = ['text/plain', 'application/json', 'text/json'];
        const accept = this.configuration.selectHeaderAccept(accepts);
        if (accept !== undefined) {
            headers = headers.set('Accept', accept);
        }
        if (withContentType) {
            const ct = this.configuration.selectHeaderContentType(['application/json', 'text/json', 'application/_*+json']);
            if (ct !== undefined) {
                headers = headers.set('Content-Type', ct);
            }
        }
        return headers;
    }

    /**
     * List custom groups in the current workspace.
     */
    public groupGet(xTenantId: string, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling groupGet.');
        }
        return this.httpClient.request<GroupDtoListWebApiResponse>('get', `${this.basePath}/api/v1/Group`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Create a group. POST /api/v1/Group — body matches GroupCreateCommand (nested `details`).
     */
    public groupPost(xTenantId: string, body: GroupCreateCommand, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling groupPost.');
        }
        return this.httpClient.request<GroupDtoWebApiResponse>('post', `${this.basePath}/api/v1/Group`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId, true),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Update a group. PUT /api/v1/Group — body is the FLAT GroupUpdateCommand.
     */
    public groupPut(xTenantId: string, body: GroupUpdateCommand, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling groupPut.');
        }
        return this.httpClient.request<GroupDtoWebApiResponse>('put', `${this.basePath}/api/v1/Group`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId, true),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Soft-delete a group. DELETE /api/v1/Group/{id}.
     */
    public groupDelete(xTenantId: string, id: string, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling groupDelete.');
        }
        if (!id) {
            throw new Error('Required parameter id was null or undefined when calling groupDelete.');
        }
        return this.httpClient.request<BooleanWebApiResponse>('delete', `${this.basePath}/api/v1/Group/${encodeURIComponent(id)}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }
}
